// Copyright ©️ 2026 eVolve MEP, LLC

import type { WorkOrderItem } from '@evolvemep/api-ts';
import { Checkbox, Flex, NumberInput, Stack, Text, useMantineTheme } from '@mantine/core';

import { EntityStatusBadge } from 'components/Mantine/EntityStatusBadge';
import { isNotNil } from 'helpers/isNotNil';
import { useUnitOfMeasure } from 'hooks/typeCacheHooks/useUnitOfMeasure';
import { getRemainingItemsToShip, getTaskStatusFromItem } from 'modules/Shop/WorkOrders/WorkOrderDetail/common';
import { ShipmentAllocationBadge } from 'modules/Shop/WorkOrders/WorkOrderDetail/Items/ShipmentAllocationBadge';
import { WorkOrderItemTypeIcon } from 'modules/Shop/WorkOrders/WorkOrderDetail/Items/WorkOrderItemTypeIcon';

type Props = {
  item: WorkOrderItem;
  selectedQuantity?: number;
  disabled?: boolean;
  onToggle: () => void;
  onQuantityChange: (qty: number) => void;
};

export const ShipmentWorkOrderItemRow = ({
  item,
  selectedQuantity,
  disabled: parentDisabled,
  onToggle,
  onQuantityChange,
}: Props) => {
  const { uoms } = useUnitOfMeasure();
  const uomCode = uoms?.find((u) => u.unitOfMeasureId === item.unitOfMeasureId)?.code ?? '';
  const remaining = getRemainingItemsToShip(item);
  const fullyAllocated = remaining === 0;
  const selected = isNotNil(selectedQuantity);
  const disabled = parentDisabled || fullyAllocated;
  const theme = useMantineTheme();

  return (
    <Flex
      align="center"
      gap="sm"
      px="sm"
      py="sm"
      bg="gray.1"
      style={{
        boxShadow: selected ? `inset 0 0 0 2px var(--mantine-color-${theme.primaryColor}-6)` : undefined,
        borderRadius: 8,
        opacity: !selected && disabled ? 0.5 : undefined,
      }}
    >
      <Flex gap="sm" align="center" miw={128}>
        <Checkbox checked={selected} onChange={onToggle} disabled={disabled} />
        {selected ? (
          <NumberInput
            w={80}
            size="xs"
            min={1}
            max={remaining}
            value={selectedQuantity}
            disabled={disabled}
            onChange={(v) => onQuantityChange(typeof v === 'number' ? v : remaining || item.totalQuantity)}
          />
        ) : (
          <Text
            fz="xs"
            c="dimmed"
            px="xs"
            py={2}
            style={{
              border: '1px solid var(--mantine-color-gray-3)',
              borderRadius: 4,
              background: 'white',
              whiteSpace: 'nowrap',
            }}
          >
            {item.totalQuantity} {uomCode}
          </Text>
        )}
      </Flex>
      <WorkOrderItemTypeIcon item={item} />
      <Stack gap={0} flex={1}>
        <Text fz="sm">{item.name}</Text>
        <Text fz="xs" c="dimmed">
          {item.totalQuantity} {uomCode}
        </Text>
      </Stack>
      <ShipmentAllocationBadge item={item} />
      <EntityStatusBadge customStatusEntityName="Task" stateName={getTaskStatusFromItem(item)} />
    </Flex>
  );
};
